import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { loadCreditMiner } from './credit-miner';
import { CREDIT_MINER_SPEC, toggleOverride } from './dev-feature-toggles';
import { useBasePath } from './use-base-path';

/**
 * Client read of the Credit Miner gate (`/api/flags/credit-miner`): whether the image-render placeholder
 * runs the game or the shimmer.
 *
 * Fails CLOSED to the shimmer. A dev-tools Feature toggles override wins over the server so the game can
 * be exercised in any browser that has dev tools unlocked; a real user always gets the server's answer.
 * Memoized per page load: a placeholder that started as a shimmer stays one — swapping the game in under
 * a person mid-render would be worse than either arm.
 */
const GATE_TIMEOUT_MS = 2500;

let cached: Promise<boolean> | null = null;
/** The answer once known, so a row that remounts renders the right arm at once instead of flashing the shimmer. */
let known: boolean | null = null;
let attempts = 0;

export function creditMinerEnabled(): Promise<boolean> {
  const forced = toggleOverride(CREDIT_MINER_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  // Only a real answer is remembered for the page. A read that failed is closed for now and asked again by the next
  // placeholder, so one blip does not hide the game until a reload. The phones follow the same rule.
  const attempt = ++attempts;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/credit-miner`, { credentials: 'include', signal: AbortSignal.timeout(GATE_TIMEOUT_MS) });
      if (res.ok) return (known = ((await res.json()) as boolean) === true);
    } catch {
      // closed for now; see above
    }
    if (attempts === attempt) cached = null;
    return false;
  })();
  return cached;
}

export function useCreditMinerEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => {
    const forced = toggleOverride(CREDIT_MINER_SPEC);
    return forced !== undefined ? forced === 'true' : (known ?? false);
  });
  useEffect(() => {
    let live = true;
    void creditMinerEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}

/** Fetches the game's script and stylesheet as soon as the gate opens, so a picture's placeholder does not wait on
 *  them before it can show the game. */
export function usePreloadCreditMiner(): void {
  const basePath = useBasePath();
  useEffect(() => {
    void creditMinerEnabled().then((on) => {
      if (on) loadCreditMiner(basePath).catch(() => undefined);
    });
  }, [basePath]);
}
